import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  requestPasswordResetOtp,
  resetPassword,
  verifyPasswordResetOtp,
} from "../services/authService";

const emailIsValid = (email) => /^\S+@\S+\.\S+$/.test(email);

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);

  const requestOtp = async (event) => {
    event?.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!emailIsValid(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await requestPasswordResetOtp({ email: normalizedEmail });
      setEmail(normalizedEmail);
      setStep("otp");
      setNotice("We sent a 6-digit verification code to your email. It expires in 10 minutes.");
    } catch (requestError) {
      setError(requestError.message || "Unable to send the verification code.");
    } finally {
      setSaving(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) {
      setError("Enter the 6-digit verification code.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      const token = await verifyPasswordResetOtp({ email, otp: otp.trim() });
      if (!token) throw new Error("Verification succeeded but no reset token was returned.");
      setResetToken(token);
      setStep("password");
      setNotice("Code verified. Choose your new password.");
    } catch (requestError) {
      setError(requestError.message || "Unable to verify the code.");
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (newPassword.length < 6) {
      setError("New password must contain at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await resetPassword({ token: resetToken, newPassword });
      setStep("complete");
      setNotice("Your password has been reset successfully. You can now sign in.");
    } catch (requestError) {
      setError(requestError.message || "Unable to reset your password.");
    } finally {
      setSaving(false);
    }
  };

  const heading = {
    email: "Forgot your password?",
    otp: "Verify your email",
    password: "Set a new password",
    complete: "Password reset",
  }[step];

  return (
    <main className="login-shell login-shell-centered">
      <section className="login-form-panel">
        <div className="login-card">
          <img className="login-card-logo" src="/assets/img/kare-logo.png" alt="Kare" />
          <div className="login-card-heading">
            <h2>{heading}</h2>
            {step === "email" && <p>Enter your administrator email and we’ll send a verification code.</p>}
            {step === "otp" && <p>Enter the code sent to <strong>{email}</strong>.</p>}
            {step === "password" && <p>Your verified reset session expires in 15 minutes.</p>}
          </div>

          {notice && <div className="alert alert-success py-2 small" role="status">{notice}</div>}
          {error && <div className="login-form-error" role="alert">{error}</div>}

          {step === "email" && (
            <form onSubmit={requestOtp} noValidate>
              <label htmlFor="reset-email">Email address</label>
              <div className="login-input-wrap">
                <i className="bi bi-envelope" />
                <input id="reset-email" type="email" value={email} onChange={(event) => { setEmail(event.target.value); setError(""); }} placeholder="name@company.com" autoComplete="email" disabled={saving} />
              </div>
              <button className="login-submit" type="submit" disabled={saving}>{saving ? "Sending code…" : "Send verification code"}</button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verifyOtp} noValidate>
              <label htmlFor="reset-otp">Verification code</label>
              <div className="login-input-wrap">
                <i className="bi bi-shield-lock" />
                <input id="reset-otp" type="text" inputMode="numeric" pattern="[0-9]*" maxLength="6" value={otp} onChange={(event) => { setOtp(event.target.value.replace(/\D/g, "")); setError(""); }} placeholder="000000" autoComplete="one-time-code" disabled={saving} />
              </div>
              <button className="login-submit" type="submit" disabled={saving}>{saving ? "Verifying…" : "Verify code"}</button>
              <div className="login-form-meta"><button type="button" onClick={requestOtp} disabled={saving}>Resend code</button><button type="button" onClick={() => { setStep("email"); setNotice(""); setError(""); }}>Change email</button></div>
            </form>
          )}

          {step === "password" && (
            <form onSubmit={savePassword} noValidate>
              <label htmlFor="reset-new-password">New password</label>
              <div className="login-input-wrap"><i className="bi bi-lock" /><input id="reset-new-password" type="password" value={newPassword} onChange={(event) => { setNewPassword(event.target.value); setError(""); }} autoComplete="new-password" disabled={saving} /></div>
              <label htmlFor="reset-confirm-password">Confirm new password</label>
              <div className="login-input-wrap"><i className="bi bi-lock-fill" /><input id="reset-confirm-password" type="password" value={confirmPassword} onChange={(event) => { setConfirmPassword(event.target.value); setError(""); }} autoComplete="new-password" disabled={saving} /></div>
              <button className="login-submit" type="submit" disabled={saving}>{saving ? "Resetting password…" : "Reset password"}</button>
            </form>
          )}

          {step === "complete" && <button className="login-submit" type="button" onClick={() => navigate("/login", { replace: true })}>Back to sign in</button>}

          {step !== "complete" && <p className="login-support"><button type="button" className="link-text" onClick={() => navigate("/login")}>Back to sign in</button></p>}
        </div>
      </section>
    </main>
  );
}
